import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, MapPin } from "lucide-react";
import { PHASE_ACCENT, overallProgress } from "@/lib/roadmapData";
import useRoadmap from "@/lib/useRoadmap";

/**
 * Compact roadmap teaser for the Home page.
 * Shows the phase node trail with a CTA to the full /roadmap page.
 */
export default function RoadmapTeaser() {
  const { t } = useTranslation();
  const { phases } = useRoadmap();
  const stats = overallProgress(phases);
  const { pct: overallPct, totalFeatures, shipped: done, inBuild: wip } = stats;

  return (
    <section
      className="relative max-w-7xl mx-auto px-4 md:px-6 pt-3 md:pt-4 pb-10 md:pb-14"
      data-testid="roadmap-teaser"
    >
      <div className="panel corner-frame relative overflow-hidden p-6 md:p-9 lg:p-10">
        {/* Ambient glow */}
        <div className="absolute -left-32 -top-32 w-96 h-96 rounded-full bg-[radial-gradient(circle,_rgba(102,230,255,0.18)_0%,_transparent_70%)] pointer-events-none" />
        <div className="absolute -right-24 -bottom-24 w-80 h-80 rounded-full bg-[radial-gradient(circle,_rgba(58,160,255,0.16)_0%,_transparent_70%)] pointer-events-none" />

        <div className="relative">
          {/* Header */}
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6 md:mb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 border border-[var(--t-border-glow)] mb-3">
                <MapPin className="w-3 h-3 text-[var(--t-cyan)]" />
                <span className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] text-[var(--t-cyan)] uppercase">
                  {t("home.roadmapEyebrow")}
                </span>
              </div>
              <h2 className="font-display text-2xl md:text-4xl text-[var(--t-text)] uppercase leading-tight">
                {t("home.roadmapTitle")}
              </h2>
              <p className="mt-2 md:mt-3 text-sm md:text-base text-[var(--t-text-dim)] max-w-xl">
                {t("home.roadmapSummaryPhases", { count: phases.length })}. {t("home.roadmapSummaryAreas", { count: totalFeatures })}.{" "}
                <span className="text-[#5fd99a] font-mono">{t("home.roadmapShipped", { count: done })}</span>
                {" · "}
                <span className="text-[#f3c552] font-mono">{t("home.roadmapInBuild", { count: wip })}</span>.
              </p>
            </div>
            <Link
              to="/roadmap"
              className="btn-primary !justify-center group"
              data-testid="roadmap-teaser-cta"
            >
              {t("home.roadmapViewFull")}{" "}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          {/* Overall bar */}
          <div className="mb-7 md:mb-8" data-testid="roadmap-teaser-progress">
            <div className="flex items-center justify-between mb-1.5 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em]">
              <span className="text-[var(--t-text-dim)]">{t("home.roadmapOverall")}</span>
              <span className="text-[var(--t-cyan)]">{overallPct}%</span>
            </div>
            <div className="h-1.5 w-full bg-[var(--t-panel-2)] border border-[var(--t-border)] overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: `${overallPct}%`,
                  background:
                    "linear-gradient(90deg, var(--t-blue-deep) 0%, var(--t-blue) 50%, var(--t-cyan) 100%)",
                  boxShadow: "0 0 12px rgba(102,230,255,0.5)",
                }}
              />
            </div>
          </div>

          {/* Phase node trail */}
          <ol className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-3 lg:gap-4 relative">
            {/* Connecting line (desktop only) */}
            <span
              className="hidden md:block absolute left-0 right-0 top-[18px] h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, var(--t-border-glow) 10%, var(--t-border-glow) 90%, transparent 100%)",
              }}
              aria-hidden
            />

            {phases.map(phase => {
              const color = PHASE_ACCENT[phase.status];
              const isDim = phase.status === "planned";
              return (
                <li
                  key={phase.number}
                  className="relative flex md:block items-start gap-3 md:gap-0 md:text-center"
                  data-testid={`roadmap-teaser-phase-${phase.number}`}
                >
                  {/* Node */}
                  <span
                    className="relative z-10 inline-flex items-center justify-center w-9 h-9 md:w-9 md:h-9 md:mx-auto rounded-full font-display text-sm shrink-0"
                    style={{
                      backgroundColor: isDim ? "var(--t-bg-2)" : color,
                      color: isDim ? color : "#0a1428",
                      border: `2px solid ${color}`,
                      boxShadow: isDim
                        ? `0 0 0 4px rgba(94,111,140,0.08)`
                        : `0 0 0 5px ${color}1f, 0 0 16px ${color}80`,
                    }}
                  >
                    {phase.number}
                  </span>

                  {/* Label */}
                  <div className="md:mt-3">
                    <div
                      className="font-mono text-[9px] md:text-[10px] tracking-[0.22em] uppercase mb-0.5"
                      style={{ color }}
                    >
                      {t("home.roadmapPhasePrefix")} {phase.number}
                    </div>
                    <div className="font-display text-sm md:text-[15px] text-[var(--t-text)] leading-tight">
                      {t(`roadmap.phases.${phase.number}.title`, { defaultValue: phase.title })}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
